import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { 
  User, BookOpen, ClipboardList, Award, MessageSquare, 
  CheckCircle, Clock, AlertCircle, Save, Send, ArrowLeft,
  FileText, Star, Shield, AlertTriangle, Lock, Unlock, History
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default OSCE Stations Configuration
const DEFAULT_OSCE_STATIONS = [
  { station_id: 'station_1', station_name: 'Patient Assessment & History', is_critical: false, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_2', station_name: 'Facial Anatomy & Injection Sites', is_critical: true, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_3', station_name: 'Injection Technique', is_critical: true, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_4', station_name: 'Complication Recognition', is_critical: true, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_5', station_name: 'Emergency Management', is_critical: true, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_6', station_name: 'Patient Communication', is_critical: false, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_7', station_name: 'Documentation & Consent', is_critical: false, score: null, critical_error: false, feedback: '' },
  { station_id: 'station_8', station_name: 'Post-Procedure Care', is_critical: false, score: null, critical_error: false, feedback: '' },
];

const WORKFLOW_STATUS_LABELS = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  submitted_to_admin: { label: 'Submitted to Admin', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
};

export default function CandidateAssessmentPage() {
  const { candidateId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  
  const [scoring, setScoring] = useState({
    candidate_id: candidateId,
    workflow_status: 'draft',
    // MCQ
    mcq_score: null,
    mcq_total: null,
    mcq_percentage: null,
    mcq_passed: null,
    mcq_complication_score: null,
    mcq_complication_passed: null,
    // OSCE Stations
    osce_stations: DEFAULT_OSCE_STATIONS,
    osce_station_average: null,
    osce_critical_pass: null,
    osce_grade: null,
    osce_feedback: '',
    // Case Logs
    case_log_average: null,
    case_log_count: null,
    case_log_safety_issues: 0,
    // Viva (Enhanced)
    viva_knowledge: 5,
    viva_communication: 5,
    viva_clinical_reasoning: 5,
    viva_patient_safety: 5,
    viva_complication_management: 5,
    viva_overall: 5,
    viva_feedback: '',
    // Ethics (Enhanced)
    ethics_knowledge: 5,
    ethics_governance: 5,
    ethics_professionalism: 5,
    ethics_consent_process: 5,
    ethics_adverse_event_handling: 5,
    ethics_overall: 5,
    ethics_feedback: '',
    ethics_unethical_justification: false,
    // Results
    final_result: 'Pending',
    fail_triggers: [],
    safety_critical_fail: false,
    submitted_to_admin: false,
    is_locked: false,
    // Second Examiner
    second_examiner_confirmed: false,
    second_examiner_name: null,
  });

  useEffect(() => {
    if (token && candidateId) {
      fetchData();
    }
  }, [token, candidateId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/examiner/candidate-full-assessment/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      
      // Load existing scoring
      if (res.data.comprehensive_scoring) {
        const existingScoring = res.data.comprehensive_scoring;
        
        // Get fresh MCQ percentage from MCQ results (fix for legacy corrupted data)
        let freshMcqPercentage = existingScoring.mcq_percentage;
        if (res.data.mcq_results?.length > 0) {
          const latest = res.data.mcq_results[0];
          freshMcqPercentage = latest.score || 0; // score is already a percentage (0-100)
        }
        // Cap mcq_percentage at 100 to fix legacy data issues
        if (freshMcqPercentage > 100) {
          freshMcqPercentage = Math.min(freshMcqPercentage, 100);
        }
        
        setScoring(prev => ({
          ...prev,
          ...existingScoring,
          mcq_percentage: freshMcqPercentage,
          mcq_passed: freshMcqPercentage >= 60,
          osce_stations: existingScoring.osce_stations || DEFAULT_OSCE_STATIONS
        }));
      }
      
      // Auto-calculate MCQ if results exist (also handles case where no existing scoring)
      if (res.data.mcq_results?.length > 0) {
        const latest = res.data.mcq_results[0];
        // latest.score is already a percentage (0-100)
        const percentage = Math.min(latest.score || 0, 100); // Cap at 100
        setScoring(prev => ({
          ...prev,
          mcq_score: latest.correct_count || Math.round(percentage),
          mcq_total: latest.total_questions,
          mcq_percentage: percentage,
          mcq_passed: percentage >= 60
        }));
      }
      
      // Auto-calculate case log average
      if (res.data.case_logs?.length > 0) {
        const scored = res.data.case_logs.filter(l => l.scoring);
        if (scored.length > 0) {
          const avg = scored.reduce((acc, l) => acc + l.scoring.overall_score, 0) / scored.length;
          const safetyIssues = scored.filter(l => l.scoring?.patient_safety_score < 5).length;
          setScoring(prev => ({
            ...prev,
            case_log_average: avg,
            case_log_count: scored.length,
            case_log_safety_issues: safetyIssues
          }));
        }
      }
    } catch (err) {
      toast.error('Failed to load candidate data');
    } finally {
      setLoading(false);
    }
  };

  const updateOSCEStation = (stationId, field, value) => {
    setScoring(prev => ({
      ...prev,
      osce_stations: prev.osce_stations.map(s => 
        s.station_id === stationId ? { ...s, [field]: value } : s
      )
    }));
  };

  const saveScoring = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/examiner/save-comprehensive-score`, scoring, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scores saved successfully');
      if (res.data.fail_triggers?.length > 0) {
        toast.warning(`Safety triggers detected: ${res.data.fail_triggers.join(', ')}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save scores');
    } finally {
      setSaving(false);
    }
  };

  const submitToAdmin = async () => {
    if (scoring.final_result === 'Pending') {
      toast.error('All assessments must be completed before submitting');
      return;
    }
    try {
      await axios.post(`${API}/examiner/submit-scores-to-admin/${candidateId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scores submitted to admin for certification');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const confirmAsSecondExaminer = async () => {
    try {
      await axios.post(`${API}/examiner/confirm-scores/${candidateId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Second examiner confirmation recorded');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><p>Candidate not found</p></div>;
  }

  const { candidate, application, mcq_results, osce, case_logs, comprehensive_scoring } = data;
  const isLocked = scoring.is_locked || scoring.workflow_status === 'approved';
  const workflowInfo = WORKFLOW_STATUS_LABELS[scoring.workflow_status] || WORKFLOW_STATUS_LABELS.draft;
  const canConfirmAsSecondExaminer = user?.id !== scoring.examiner_id && !scoring.second_examiner_confirmed;

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="candidate-assessment-page">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-8">
        <div className="imcac-container">
          <Button variant="ghost" onClick={() => navigate('/examiner')} className="text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Candidate Assessment</p>
              <h1 className="text-3xl font-bold text-white tracking-tight font-heading">{candidate.full_name}</h1>
              <p className="text-slate-300 text-sm mt-1">{candidate.email}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge className="bg-white/10 text-white">{application?.examination || application?.specialty}</Badge>
                <Badge className="bg-white/10 text-white">Route {application?.route}</Badge>
                <Badge className={workflowInfo.color}>{workflowInfo.label}</Badge>
                {isLocked && <Badge className="bg-red-500 text-white"><Lock className="h-3 w-3 mr-1" />Locked</Badge>}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Badge className={`text-lg px-4 py-2 ${scoring.final_result === 'Pass' ? 'bg-green-500' : scoring.final_result === 'Fail' ? 'bg-red-500' : 'bg-amber-500'} text-white`}>
                {scoring.final_result}
              </Badge>
              {scoring.award_classification && (
                <Badge className="bg-[#C5A572] text-white">{scoring.award_classification}</Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Safety Fail Alert */}
        {scoring.safety_critical_fail && (
          <div className="bg-red-100 border-2 border-red-400 rounded-sm p-4 mb-6 flex items-start gap-3" data-testid="safety-fail-alert">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">Safety-Critical Failure Detected</p>
              <p className="text-sm text-red-700 mt-1">This candidate has triggered one or more safety-critical fail conditions:</p>
              <ul className="list-disc list-inside text-sm text-red-700 mt-2">
                {scoring.fail_triggers?.map((trigger, idx) => (
                  <li key={idx}>{trigger}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Workflow Status Bar */}
        {scoring.submitted_to_admin && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-blue-800">Scores Submitted to Admin</p>
                <p className="text-xs text-blue-600">Status: {workflowInfo.label}</p>
              </div>
            </div>
            {scoring.second_examiner_confirmed ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="h-3 w-3 mr-1" /> 2nd Examiner: {scoring.second_examiner_name}
              </Badge>
            ) : canConfirmAsSecondExaminer && (
              <Button onClick={confirmAsSecondExaminer} size="sm" variant="outline" className="border-blue-400 text-blue-700">
                Confirm as 2nd Examiner
              </Button>
            )}
          </div>
        )}

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="w-full justify-start h-auto flex-wrap bg-white border border-slate-200 rounded-sm p-1 gap-1">
            <TabsTrigger value="overview" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <User className="h-4 w-4 mr-1" /> Overview
            </TabsTrigger>
            <TabsTrigger value="mcq" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <BookOpen className="h-4 w-4 mr-1" /> MCQ
            </TabsTrigger>
            <TabsTrigger value="osce" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Award className="h-4 w-4 mr-1" /> OSCE Stations
            </TabsTrigger>
            <TabsTrigger value="caselogs" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <ClipboardList className="h-4 w-4 mr-1" /> Case Logs
            </TabsTrigger>
            <TabsTrigger value="viva" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <MessageSquare className="h-4 w-4 mr-1" /> Viva Voce
            </TabsTrigger>
            <TabsTrigger value="ethics" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Shield className="h-4 w-4 mr-1" /> Ethics
            </TabsTrigger>
            {comprehensive_scoring?.audit_log?.length > 0 && (
              <TabsTrigger value="audit" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <History className="h-4 w-4 mr-1" /> Audit Log
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* MCQ Summary */}
              <Card className="imcac-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-sm bg-blue-100 flex items-center justify-center">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-[#1B2A4A]">MCQ Examination</h3>
                  </div>
                  {scoring.mcq_score !== null ? (
                    <div>
                      <p className="text-3xl font-bold text-[#1B2A4A]">{scoring.mcq_score}/{scoring.mcq_total}</p>
                      <p className="text-sm text-slate-500">{Math.min(scoring.mcq_percentage || 0, 100).toFixed(1)}%</p>
                      <Badge className={`mt-2 ${scoring.mcq_passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {scoring.mcq_passed ? 'Passed' : 'Failed'}
                      </Badge>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No MCQ results yet</p>
                  )}
                </CardContent>
              </Card>

              {/* OSCE Summary */}
              <Card className={`imcac-card ${scoring.osce_critical_pass === false ? 'border-red-300 bg-red-50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-sm bg-green-100 flex items-center justify-center">
                      <Award className="h-5 w-5 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-[#1B2A4A]">OSCE (8 Stations)</h3>
                  </div>
                  {scoring.osce_station_average ? (
                    <div>
                      <p className="text-3xl font-bold text-[#1B2A4A]">{scoring.osce_station_average.toFixed(1)}/10</p>
                      <p className="text-sm text-slate-500">{scoring.osce_grade}</p>
                      {scoring.osce_critical_pass === false && (
                        <Badge className="mt-2 bg-red-100 text-red-700">
                          <AlertTriangle className="h-3 w-3 mr-1" /> Critical Station Failed
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Not graded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Case Logs Summary */}
              <Card className={`imcac-card ${scoring.case_log_safety_issues >= 3 ? 'border-red-300 bg-red-50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center">
                      <ClipboardList className="h-5 w-5 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-[#1B2A4A]">Case Logs</h3>
                  </div>
                  {scoring.case_log_count > 0 ? (
                    <div>
                      <p className="text-3xl font-bold text-[#1B2A4A]">{scoring.case_log_average?.toFixed(1)}/10</p>
                      <p className="text-sm text-slate-500">{scoring.case_log_count} cases scored</p>
                      {scoring.case_log_safety_issues > 0 && (
                        <p className="text-xs text-red-600 mt-1">{scoring.case_log_safety_issues} safety concerns</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">{case_logs?.length || 0} cases submitted</p>
                  )}
                </CardContent>
              </Card>

              {/* Viva Summary */}
              <Card className={`imcac-card ${(scoring.viva_patient_safety < 5 || scoring.viva_complication_management < 5) ? 'border-red-300 bg-red-50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-[#1B2A4A]">Viva Voce</h3>
                  </div>
                  {scoring.viva_overall > 0 && comprehensive_scoring ? (
                    <div>
                      <p className="text-3xl font-bold text-[#1B2A4A]">{scoring.viva_overall}/10</p>
                      <Badge className={`mt-2 ${scoring.viva_overall >= 5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {scoring.viva_overall >= 5 ? 'Passed' : 'Failed'}
                      </Badge>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Not scored yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Ethics Summary */}
              <Card className={`imcac-card ${scoring.ethics_unethical_justification ? 'border-red-300 bg-red-50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-sm bg-indigo-100 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-indigo-600" />
                    </div>
                    <h3 className="font-semibold text-[#1B2A4A]">Ethics & Governance</h3>
                  </div>
                  {scoring.ethics_overall > 0 && comprehensive_scoring ? (
                    <div>
                      <p className="text-3xl font-bold text-[#1B2A4A]">{scoring.ethics_overall}/10</p>
                      {scoring.ethics_unethical_justification && (
                        <Badge className="mt-2 bg-red-100 text-red-700">
                          <AlertTriangle className="h-3 w-3 mr-1" /> Unethical Justification
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Not scored yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Final Result */}
              <Card className={`imcac-card ${scoring.final_result === 'Pass' ? 'border-green-300 bg-green-50' : scoring.final_result === 'Fail' ? 'border-red-300 bg-red-50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${scoring.final_result === 'Pass' ? 'bg-green-200' : scoring.final_result === 'Fail' ? 'bg-red-200' : 'bg-slate-100'}`}>
                      <Star className={`h-5 w-5 ${scoring.final_result === 'Pass' ? 'text-green-600' : scoring.final_result === 'Fail' ? 'text-red-600' : 'text-slate-600'}`} />
                    </div>
                    <h3 className="font-semibold text-[#1B2A4A]">Final Result</h3>
                  </div>
                  <p className={`text-3xl font-bold ${scoring.final_result === 'Pass' ? 'text-green-600' : scoring.final_result === 'Fail' ? 'text-red-600' : 'text-amber-600'}`}>
                    {scoring.final_result}
                  </p>
                  {scoring.total_weighted_score && (
                    <p className="text-sm text-slate-500 mt-1">
                      Weighted Score: {Math.min(scoring.total_weighted_score, 100).toFixed(1)}%
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <Button onClick={saveScoring} disabled={saving || isLocked} className="bg-[#1B2A4A] text-white rounded-sm">
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save All Scores'}
              </Button>
              {!scoring.submitted_to_admin && (
                <Button onClick={submitToAdmin} disabled={scoring.final_result === 'Pending' || isLocked} className="bg-[#C5A572] text-white rounded-sm">
                  <Send className="h-4 w-4 mr-1" /> Submit to Admin
                </Button>
              )}
              {isLocked && (
                <Badge className="bg-red-100 text-red-700 px-3 py-2">
                  <Lock className="h-4 w-4 mr-1" /> Scores Locked
                </Badge>
              )}
            </div>
          </TabsContent>

          {/* MCQ Tab */}
          <TabsContent value="mcq">
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">MCQ Examination Results</h3>
                {mcq_results?.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Percentage</TableHead>
                          <TableHead>Result</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mcq_results.map((result, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{new Date(result.completed_at).toLocaleDateString()}</TableCell>
                            <TableCell className="font-semibold">{result.correct_count || result.score}</TableCell>
                            <TableCell>{result.total_questions}</TableCell>
                            <TableCell>{result.score?.toFixed(1) || ((result.correct_count / result.total_questions) * 100).toFixed(1)}%</TableCell>
                            <TableCell>
                              <Badge className={`${(result.score >= 60 || (result.correct_count / result.total_questions) >= 0.6) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {(result.score >= 60 || (result.correct_count / result.total_questions) >= 0.6) ? 'Pass' : 'Fail'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {/* MCQ Complication Domain */}
                    <div className="mt-6 p-4 bg-slate-50 rounded-sm">
                      <h4 className="font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        Safety-Critical: Complication Domain
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Complication Domain Score (%)</Label>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={scoring.mcq_complication_score || ''}
                            onChange={e => setScoring({...scoring, mcq_complication_score: parseFloat(e.target.value) || null})}
                            className="mt-1 rounded-sm"
                            disabled={isLocked}
                            placeholder="Enter complication domain %"
                          />
                        </div>
                        <div className="flex items-end gap-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={scoring.mcq_complication_passed || false}
                              onCheckedChange={v => setScoring({...scoring, mcq_complication_passed: v})}
                              disabled={isLocked}
                            />
                            <Label className="text-sm">Passed Safety Domain (≥70%)</Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 py-8 text-center">No MCQ exam results yet.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* OSCE Stations Tab */}
          <TabsContent value="osce">
            <Card className="imcac-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">OSCE Station-wise Assessment</h3>
                  {scoring.osce_station_average && (
                    <Badge className={scoring.osce_critical_pass === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                      Average: {scoring.osce_station_average.toFixed(1)}/10 | {scoring.osce_grade}
                    </Badge>
                  )}
                </div>
                <div className="space-y-4">
                  {scoring.osce_stations.map((station) => (
                    <div key={station.station_id} className={`border rounded-sm p-4 ${station.is_critical ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[#1B2A4A]">{station.station_name}</h4>
                          {station.is_critical && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Safety Critical</Badge>
                          )}
                        </div>
                        {station.score !== null && (
                          <Badge className={station.score >= 5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {station.score}/10
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm">Score (0-10)</Label>
                          <Input
                            type="number"
                            min={0}
                            max={10}
                            value={station.score ?? ''}
                            onChange={e => updateOSCEStation(station.station_id, 'score', parseInt(e.target.value) || null)}
                            className="mt-1 rounded-sm"
                            disabled={isLocked}
                          />
                        </div>
                        {station.is_critical && (
                          <div className="flex items-end">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={station.critical_error || false}
                                onCheckedChange={v => updateOSCEStation(station.station_id, 'critical_error', v)}
                                disabled={isLocked}
                              />
                              <Label className="text-sm text-red-600">Critical Error (Auto-Fail)</Label>
                            </div>
                          </div>
                        )}
                        <div>
                          <Label className="text-sm">Feedback</Label>
                          <Input
                            value={station.feedback || ''}
                            onChange={e => updateOSCEStation(station.station_id, 'feedback', e.target.value)}
                            className="mt-1 rounded-sm"
                            placeholder="Station feedback..."
                            disabled={isLocked}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <Label className="text-sm">Overall OSCE Feedback</Label>
                  <textarea
                    value={scoring.osce_feedback || ''}
                    onChange={e => setScoring({...scoring, osce_feedback: e.target.value})}
                    className="w-full mt-1 p-2 border rounded-sm text-sm"
                    rows={3}
                    placeholder="Overall feedback on OSCE performance..."
                    disabled={isLocked}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Case Logs Tab */}
          <TabsContent value="caselogs">
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Case Log Portfolio</h3>
                {case_logs?.length > 0 ? (
                  <div className="space-y-4">
                    {case_logs.map((log) => (
                      <div key={log.id} className="border border-slate-200 rounded-sm p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-[#1B2A4A]">{log.procedure_type}</h4>
                            <p className="text-sm text-slate-500">Patient: {log.patient_name}, {log.patient_age}y, {log.patient_gender}</p>
                          </div>
                          <Badge className={`${log.status === 'scored' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {log.status}
                          </Badge>
                        </div>
                        {log.scoring && (
                          <div className="grid grid-cols-5 gap-2 text-xs">
                            <div className="text-center p-2 bg-slate-50 rounded">
                              <p className="font-semibold">{log.scoring.clinical_reasoning_score}/10</p>
                              <p className="text-slate-500">Clinical</p>
                            </div>
                            <div className="text-center p-2 bg-slate-50 rounded">
                              <p className="font-semibold">{log.scoring.technique_score}/10</p>
                              <p className="text-slate-500">Technique</p>
                            </div>
                            <div className="text-center p-2 bg-slate-50 rounded">
                              <p className="font-semibold">{log.scoring.documentation_score}/10</p>
                              <p className="text-slate-500">Docs</p>
                            </div>
                            <div className={`text-center p-2 rounded ${log.scoring.patient_safety_score < 5 ? 'bg-red-50' : 'bg-slate-50'}`}>
                              <p className={`font-semibold ${log.scoring.patient_safety_score < 5 ? 'text-red-600' : ''}`}>{log.scoring.patient_safety_score}/10</p>
                              <p className="text-slate-500">Safety</p>
                            </div>
                            <div className="text-center p-2 bg-green-50 rounded">
                              <p className="font-semibold text-green-700">{log.scoring.overall_score}/10</p>
                              <p className="text-green-600">Overall</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 py-8 text-center">No case logs submitted yet.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Viva Voce Tab (Enhanced) */}
          <TabsContent value="viva">
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Viva Voce & Case Defense</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm">Knowledge Score (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.viva_knowledge}
                      onChange={e => setScoring({...scoring, viva_knowledge: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Communication Score (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.viva_communication}
                      onChange={e => setScoring({...scoring, viva_communication: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Clinical Reasoning (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.viva_clinical_reasoning}
                      onChange={e => setScoring({...scoring, viva_clinical_reasoning: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  {/* Safety-Critical Fields */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <Label className="text-sm text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Patient Safety (1-10) *Critical
                    </Label>
                    <Input type="number" min={1} max={10} value={scoring.viva_patient_safety}
                      onChange={e => setScoring({...scoring, viva_patient_safety: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm border-amber-300" disabled={isLocked}
                    />
                    <p className="text-xs text-amber-600 mt-1">Score {'<'}5 triggers safety fail</p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <Label className="text-sm text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Complication Management (1-10) *Critical
                    </Label>
                    <Input type="number" min={1} max={10} value={scoring.viva_complication_management}
                      onChange={e => setScoring({...scoring, viva_complication_management: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm border-amber-300" disabled={isLocked}
                    />
                    <p className="text-xs text-amber-600 mt-1">Score {'<'}5 triggers safety fail</p>
                  </div>
                  <div>
                    <Label className="text-sm">Overall Viva Score (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.viva_overall}
                      onChange={e => setScoring({...scoring, viva_overall: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label className="text-sm">Feedback</Label>
                    <textarea value={scoring.viva_feedback || ''}
                      onChange={e => setScoring({...scoring, viva_feedback: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-sm text-sm" rows={4}
                      placeholder="Detailed feedback on viva performance..." disabled={isLocked}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ethics Tab (Enhanced) */}
          <TabsContent value="ethics">
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Ethics & Governance Assessment</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm">Ethics Knowledge (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.ethics_knowledge}
                      onChange={e => setScoring({...scoring, ethics_knowledge: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Governance Score (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.ethics_governance}
                      onChange={e => setScoring({...scoring, ethics_governance: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Professionalism (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.ethics_professionalism}
                      onChange={e => setScoring({...scoring, ethics_professionalism: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Consent Process (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.ethics_consent_process}
                      onChange={e => setScoring({...scoring, ethics_consent_process: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  {/* Safety-Critical Fields */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <Label className="text-sm text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Adverse Event Handling (1-10) *Critical
                    </Label>
                    <Input type="number" min={1} max={10} value={scoring.ethics_adverse_event_handling}
                      onChange={e => setScoring({...scoring, ethics_adverse_event_handling: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm border-amber-300" disabled={isLocked}
                    />
                    <p className="text-xs text-amber-600 mt-1">Score {'<'}5 triggers safety fail</p>
                  </div>
                  <div>
                    <Label className="text-sm">Overall Ethics Score (1-10)</Label>
                    <Input type="number" min={1} max={10} value={scoring.ethics_overall}
                      onChange={e => setScoring({...scoring, ethics_overall: parseInt(e.target.value) || 1})}
                      className="mt-1 rounded-sm" disabled={isLocked}
                    />
                  </div>
                  {/* Unethical Justification Flag */}
                  <div className="md:col-span-2 lg:col-span-3 p-4 bg-red-50 border border-red-200 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={scoring.ethics_unethical_justification || false}
                        onCheckedChange={v => setScoring({...scoring, ethics_unethical_justification: v})}
                        disabled={isLocked}
                      />
                      <div>
                        <Label className="text-sm text-red-800 font-semibold">Unethical Practice Justification Detected</Label>
                        <p className="text-xs text-red-600">Check this if candidate justified unethical practices. This triggers automatic fail.</p>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label className="text-sm">Feedback</Label>
                    <textarea value={scoring.ethics_feedback || ''}
                      onChange={e => setScoring({...scoring, ethics_feedback: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-sm text-sm" rows={4}
                      placeholder="Detailed feedback on ethics and governance assessment..." disabled={isLocked}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          {comprehensive_scoring?.audit_log?.length > 0 && (
            <TabsContent value="audit">
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Audit Log</h3>
                  <div className="space-y-3">
                    {comprehensive_scoring.audit_log.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm">
                        <History className="h-4 w-4 text-slate-500 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#1B2A4A]">{entry.action.replace(/_/g, ' ').toUpperCase()}</p>
                          <p className="text-xs text-slate-500">By: {entry.by_name || entry.by}</p>
                          {entry.notes && <p className="text-xs text-slate-600 mt-1">{entry.notes}</p>}
                        </div>
                        <p className="text-xs text-slate-400">{new Date(entry.timestamp).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
